import { useEffect, useRef } from "react";
import { useIntl, FormattedMessage, defineMessages } from "react-intl";
import { fromEvent, debounceTime, merge } from "rxjs";

import SROnly from "common/core/screen_reader";
import { useForm } from "common/core/form";
import { OptionBar, RadioGroup, RadioInput } from "common/core/form/option";
import { TextInput } from "common/core/form/text";
import ActionButton from "common/core/action_button";
import { findMaxRemSize, findMaxFontSize, centerAndAlignText, cropCanvas } from "util/canvas_text";
import SignatureFonts, { FONTS_V2 } from "util/signature_fonts";
import { userInitials, userFullName } from "util/user";
import { useDebouncedState } from "util/timeout";
import { b, useId } from "util/html";
import { MAX_SIGNATURE_FONT_SIZE, SIGNATURE_COLOR_HEX } from "constants/globals";
import { useFeatureFlag } from "common/feature_gating";

import type { SetAssetDataAction } from ".";
import Styles from "./type.module.scss";
import ErrorAlert from "./error_alert";

const ONLY_AUTO_FONTS = Object.freeze(
  SignatureFonts.filter(({ onlyAutogeneration }) => !onlyAutogeneration),
);
const ONLY_AUTO_FONTS_V2 = Object.freeze(
  FONTS_V2.filter(({ onlyAutogeneration }) => !onlyAutogeneration),
);

const MESSAGES = defineMessages({
  placeholder: {
    id: "bfb4f50e-d851-438f-9dcc-187a9ee4b045",
    defaultMessage: "Your {isSignature, select, true {name} other {initials}}",
  },
});

type Props = {
  currentUser: {
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
  };
  assetType: "signature" | "initials";
  setAssetData: SetAssetDataAction;
  showError: boolean;
  lockRecipientName: boolean;
};
type FontName = (typeof ONLY_AUTO_FONTS)[number]["font"];
type FormValues = {
  assetInput: string;
  font: FontName;
};

export default function Type(props: Props) {
  const measureFontInRem = useFeatureFlag("signer-rem-signatures");
  const FONTS = measureFontInRem ? ONLY_AUTO_FONTS_V2 : ONLY_AUTO_FONTS;
  const measureFn = measureFontInRem ? findMaxRemSize : findMaxFontSize;
  const { currentUser, assetType, setAssetData, showError, lockRecipientName } = props;
  const isSignature = assetType === "signature";
  const text = isSignature ? userFullName(currentUser) : userInitials(currentUser);
  const signatureRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const intl = useIntl();
  const instructionsId = useId();
  const errorId = useId();
  const { watch, register, setValue } = useForm<FormValues>({
    defaultValues: {
      assetInput: text,
    },
  });
  const assetInput = watch("assetInput");
  const selectedFont = watch("font");
  const debouncedInput = useDebouncedState(assetInput, 150);

  const clearData = () => {
    setAssetData(null);
    setValue("font", "");
  };

  const drawSignatures = (text?: string) => {
    if (!text) {
      return;
    }

    FONTS.forEach(({ font, padding }, index) => {
      const canvas = signatureRefs.current[index]!;
      const ctx = canvas.getContext("2d")!;
      const { offsetWidth, offsetHeight } = canvas;

      const ratio = Math.max(window.devicePixelRatio, 1);
      const width = offsetWidth * ratio;
      const height = offsetHeight * ratio;

      canvas.width = width;
      canvas.height = height;

      const maxFontSize = measureFn(
        text,
        font,
        canvas,
        padding * ratio,
        MAX_SIGNATURE_FONT_SIZE * ratio,
      );

      ctx.clearRect(0, 0, width, height);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `normal ${maxFontSize}${measureFontInRem ? "rem" : "px"} ${font}`;
      ctx.fillStyle = SIGNATURE_COLOR_HEX;
      ctx.strokeStyle = SIGNATURE_COLOR_HEX;
      ctx.lineWidth = ratio;

      // We draw the text and stroke in the middle of the canvas before shifting it down and to the left
      // since fonts designed to mimic handwriting have strange overhangs that aren't accounted for with
      // context.measureText();
      ctx.strokeText(text, width / 2, height / 2);
      ctx.fillText(text, width / 2, height / 2);

      // We draw the canvas once with all of our desired text and strokes, but we want to make sure
      // none of the text is cut off when we left align it.
      centerAndAlignText(text, canvas, ctx, measureFontInRem);
    });
  };

  useEffect(() => {
    if (debouncedInput) {
      drawSignatures(debouncedInput);
    }
  }, [debouncedInput]);

  const handleClearInput = () => {
    clearData();
    setValue("assetInput", "");
  };

  useEffect(() => {
    if (!selectedFont || !assetInput) {
      clearData();
      return;
    }
    const index = FONTS.findIndex(({ font }) => font === selectedFont);
    const canvas = signatureRefs.current[index];
    if (!canvas) {
      return;
    }

    const textCanvas = cropCanvas(canvas);
    setAssetData({
      image: textCanvas.toDataURL("image/png"),
      method: "type",
    });
  }, [assetInput, selectedFont]);

  useEffect(() => {
    const handleResize = () => {
      drawSignatures(debouncedInput);
    };

    const mql = window.matchMedia("(orientation: portrait)");

    const event = merge(fromEvent(window, "resize"), fromEvent(mql, "change")).pipe(
      debounceTime(100),
    );

    const subscription = event.subscribe(handleResize);

    return () => subscription.unsubscribe();
  }, [debouncedInput]);
  const scaleString = useFeatureFlag("signer-rem-signatures-scale", "1");
  const parsedScale = parseFloat(scaleString);
  // If scale is not greater than 1 for whatever reason, scale signature canvases by 1 so the signatures don't get smaller
  const scale = isNaN(parsedScale) || parsedScale < 1 ? 1 : parsedScale;

  return (
    <>
      <div>
        {!lockRecipientName && (
          <p className={Styles.instructions} id={instructionsId}>
            {isSignature ? (
              <FormattedMessage
                id="04ef1443-c6e5-437d-89bf-832d8c9038b1"
                defaultMessage="Type your name <b>as it is written below the signature line</b>"
                values={{ b }}
              />
            ) : (
              <FormattedMessage
                id="bcf17b11-18de-4e0f-b055-94e08d5c8736"
                defaultMessage="Type your initials"
              />
            )}
          </p>
        )}
        {/* We use a no-op here since form submission isn't required and we listen for formValue changes */}
        <div className={Styles.inputContainer}>
          <TextInput
            disabled={lockRecipientName}
            aria-invalid={false}
            aria-describedby={!lockRecipientName ? instructionsId : ""}
            aria-label={
              lockRecipientName ? intl.formatMessage(MESSAGES.placeholder, { isSignature }) : ""
            }
            placeholder={intl.formatMessage(MESSAGES.placeholder, {
              isSignature,
            })}
            {...register("assetInput")}
          />
          {!lockRecipientName && assetInput && (
            <ActionButton className={Styles.clearButton} onClick={handleClearInput}>
              <FormattedMessage id="a28ab8b5-66f7-4afb-8bbd-648791be1cfa" defaultMessage="Clear" />
            </ActionButton>
          )}
        </div>
        {!lockRecipientName && !debouncedInput ? (
          <div className={Styles.emptyState}>
            <FormattedMessage
              id="6aa4a357-0608-4755-856f-6973eece664a"
              defaultMessage="Type your {isSignature, select, true {full name} other {initials}} above to choose a font"
              values={{ isSignature }}
            />
          </div>
        ) : (
          <RadioGroup
            label={
              <SROnly>
                <FormattedMessage
                  id="caa64c58-9a1d-4047-9cd4-51c946c545ad"
                  defaultMessage="Font options"
                />
              </SROnly>
            }
            aria-describedby={showError ? errorId : ""}
          >
            {FONTS.map(({ font }, index) => (
              <OptionBar
                key={font}
                className={Styles.signatureOption}
                label={
                  <>
                    <SROnly>{font}</SROnly>
                    <canvas
                      style={{
                        width: `${100 * scale}%`,
                        height: `${100 * scale}%`,
                        transform: `scale(${1 / scale})`,
                      }}
                      ref={(el) => (signatureRefs.current[index] = el)}
                    />
                  </>
                }
                input={
                  <RadioInput
                    data-automation-id={font}
                    value={font}
                    {...register("font")}
                    {...(((selectedFont && selectedFont !== font) ||
                      (!selectedFont && FONTS[0].font !== font)) && { tabIndex: -1 })}
                  />
                }
              />
            ))}
          </RadioGroup>
        )}
      </div>
      <ErrorAlert id={errorId}>
        {showError && (
          <FormattedMessage
            id="88c6f074-b18c-4b66-be3f-b0b02a236acf"
            defaultMessage="Please, select a font for your {isSignature, select, true {signature} other {initials}}"
            values={{
              isSignature,
            }}
          />
        )}
      </ErrorAlert>
    </>
  );
}
